import React from "react";
import { FiAlertTriangle, FiBriefcase, FiCalendar, FiCheckCircle, FiChevronRight, FiHeart, FiShield, FiTrendingUp } from "react-icons/fi";
import { formatNaira } from "../../../../utils/dashboardFormat";
import { buildProfileSummary } from "../../../../components/MemberProfile/profileModel";
import { formatDay, titleCase } from "../../../../components/MemberProfile/parts";

const Stat = ({ label, value, note, icon: Icon, tone = "grey", quiet }) => (
  <div className={`mp-stat tone-${tone} ${quiet ? "quiet" : ""}`}>
    <span className="mp-chip-ico">
      <Icon size={16} aria-hidden="true" />
    </span>
    <div className="body">
      <p className="mp-mute">{label}</p>
      <div className="v">{value}</div>
      {note ? <p className="mp-mute" style={{ marginTop: 2 }}>{note}</p> : null}
    </div>
  </div>
);

/**
 * Overview: what needs attention first, then the loan and borrowing position.
 * The money totals themselves live in the strip above the tabs, so they are not repeated here.
 */
const OverviewTab = ({ memberDetails, savingsTotal, sharesTotal, onNavigate = () => {} }) => {
  // The tile strip and this tab read the same rules, so they cannot disagree.
  const summary = buildProfileSummary({
    ...memberDetails,
    savings: { ...(memberDetails?.savings || {}), total: savingsTotal ?? memberDetails?.savings?.total },
    shares: { ...(memberDetails?.shares || {}), total: sharesTotal ?? memberDetails?.shares?.total },
  });

  const attention = [];
  summary.overdueRows.slice(0, 3).forEach((row) => {
    attention.push({
      key: `o${row.id}`,
      tone: "bad",
      text: `${formatNaira(row.total)} overdue${row.daysOverdue > 0 ? ` by ${row.daysOverdue} days` : ""}`,
      sub: `${titleCase(row.loanType)}, due ${formatDay(row.dueAt)}`,
      go: "repayments",
    });
  });
  if (summary.overdue > 0 && summary.overdueRows.length === 0) {
    attention.push({ key: "o", tone: "bad", text: `${formatNaira(summary.overdue)} overdue`, sub: "See the repayment schedule", go: "repayments" });
  }
  if (summary.pendingLoanCount > 0 || summary.pendingLoanRequests > 0) {
    attention.push({
      key: "p",
      tone: "warn",
      text: summary.pendingLoanCount
        ? `${summary.pendingLoanCount} loan ${summary.pendingLoanCount === 1 ? "request is" : "requests are"} waiting for a decision`
        : `${formatNaira(summary.pendingLoanRequests)} in loan requests waiting`,
      sub: formatNaira(summary.pendingLoanRequests),
      go: "loans",
    });
  }

  return (
    <div>
      <h3 className="mp-h">Needs attention</h3>
      {attention.length === 0 ? (
        <div className="mp-clear">
          <span className="mp-chip-ico tone-green">
            <FiCheckCircle size={16} aria-hidden="true" />
          </span>
          <div>
            <strong>All clear</strong>
            <span>Nothing needs attention. No overdue repayments or loan requests waiting.</span>
          </div>
        </div>
      ) : (
        <ul className="mp-attn">
          {attention.map((item) => (
            <li key={item.key}>
              <FiAlertTriangle
                size={18}
                aria-hidden="true"
                style={{ color: item.tone === "bad" ? "var(--dx-bad)" : "var(--dx-warn)", flex: "none" }}
              />
              <div className="grow">
                <div>{item.text}</div>
                <div className="mp-mute">{item.sub}</div>
              </div>
              <button type="button" className="mp-link" onClick={() => onNavigate(item.go)}>
                Open <FiChevronRight size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <h3 className="mp-h mp-sec">Loans and borrowing</h3>
      <div className="mp-grid2">
        <Stat
          label="Can borrow up to"
          value={summary.borrowingPower === null ? "—" : formatNaira(summary.borrowingPower)}
          note={`Based on ${summary.multiplierLabel ? `${summary.multiplierLabel} ` : ""}savings and shares`}
          icon={FiTrendingUp}
          tone="primary"
        />
        <Stat label="Active loan balance" value={formatNaira(summary.activeLoanBalance)} icon={FiBriefcase} tone="amber" quiet={!summary.activeLoanBalance} />
        <Stat label="Upcoming repayments" value={formatNaira(summary.pendingRepayment)} icon={FiCalendar} tone="blue" quiet={!summary.pendingRepayment} />
        <Stat label="Repaid so far" value={formatNaira(summary.paidRepayment)} icon={FiCheckCircle} tone="green" quiet={!summary.paidRepayment} />
      </div>

      <h3 className="mp-h mp-sec">Account health</h3>
      <div className="mp-grid2">
        <Stat label="Membership" value={titleCase(memberDetails?.account_health?.membership || "active")} icon={FiShield} tone="green" />
        <Stat label="Savings rate" value={titleCase(memberDetails?.account_health?.savings_rate || "stable")} icon={FiHeart} tone="purple" />
      </div>
    </div>
  );
};

export default OverviewTab;
